"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import BlackPill from "@/components/getting-started/BlackPill";
import Card from "@/components/getting-started/Card";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import griBGimg from "@/public/lp2/component1/grid-bg.svg";
import blurBGimg from "@/public/lp2/component2/blur-bg.svg";
import leftArrowImg from "@/public/lp2/component2/left.svg";
import rightArrowImg from "@/public/lp2/component2/right.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function Component2Section({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const content3Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const component2TL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      component2TL
        .from(content1Ref.current.children, {
          duration: 1,
          stagger: 0.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content3Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.25,
          scrollTrigger: {
            trigger: content3Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  const swiperRef = useRef(null);
  const btnIndicator1Ref = useRef<HTMLButtonElement | any>(undefined);
  const btnIndicator2Ref = useRef<HTMLButtonElement | any>(undefined);
  const btnIndicator3Ref = useRef<HTMLButtonElement | any>(undefined);
  const btnMobileIndicator1Ref = useRef<HTMLButtonElement | any>(undefined);
  const btnMobileIndicator2Ref = useRef<HTMLButtonElement | any>(undefined);
  const btnMobileIndicator3Ref = useRef<HTMLButtonElement | any>(undefined);

  function onAutoplayTimeLeft(
    swiper: any,
    timeLeft: number,
    percentage: number
  ) {
    if (swiper.realIndex === 0) {
      btnIndicator1Ref.current.children[0].style.setProperty(
        "--progress",
        (1 - percentage) * 100
      );
      btnIndicator2Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator3Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator1Ref.current.children[1].style.visibility = "visible";
      btnIndicator2Ref.current.children[1].style.visibility = "hidden";
      btnIndicator3Ref.current.children[1].style.visibility = "hidden";
    } else if (swiper.realIndex === 1) {
      btnIndicator2Ref.current.children[0].style.setProperty(
        "--progress",
        (1 - percentage) * 100
      );
      btnIndicator1Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator3Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator2Ref.current.children[1].style.visibility = "visible";
      btnIndicator1Ref.current.children[1].style.visibility = "hidden";
      btnIndicator3Ref.current.children[1].style.visibility = "hidden";
    } else if (swiper.realIndex === 2) {
      btnIndicator3Ref.current.children[0].style.setProperty(
        "--progress",
        (1 - percentage) * 100
      );
      btnIndicator1Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator2Ref.current.children[0].style.setProperty("--progress", 0);
      btnIndicator3Ref.current.children[1].style.visibility = "visible";
      btnIndicator1Ref.current.children[1].style.visibility = "hidden";
      btnIndicator2Ref.current.children[1].style.visibility = "hidden";
    }
  }

  function goToSlide(index: number) {
    // @ts-ignore
    swiperRef.current.swiper.slideTo(index);
  }

  function goToNextSlide() {
    // @ts-ignore
    swiperRef.current.swiper.slideNext();
  }

  function goToPreviousSlide() {
    // @ts-ignore
    swiperRef.current.swiper.slidePrev();
  }

  function changeSliderIndicatorActive(index: number) {
    if (index === 0) {
      btnIndicator1Ref.current?.classList.add("active");
      btnIndicator2Ref.current?.classList.remove("active");
      btnIndicator3Ref.current?.classList.remove("active");
      // mobile
      btnMobileIndicator1Ref.current.classList.add("active");
      btnMobileIndicator2Ref.current.classList.remove("active");
      btnMobileIndicator3Ref.current.classList.remove("active");
    } else if (index === 1) {
      btnIndicator1Ref.current?.classList.remove("active");
      btnIndicator2Ref.current?.classList.add("active");
      btnIndicator3Ref.current?.classList.remove("active");
      // mobile
      btnMobileIndicator2Ref.current.classList.add("active");
      btnMobileIndicator1Ref.current.classList.remove("active");
      btnMobileIndicator3Ref.current.classList.remove("active");
    } else if (index === 2) {
      btnIndicator1Ref.current?.classList.remove("active");
      btnIndicator2Ref.current?.classList.remove("active");
      btnIndicator3Ref.current?.classList.add("active");
      // mobile
      btnMobileIndicator3Ref.current.classList.add("active");
      btnMobileIndicator1Ref.current.classList.remove("active");
      btnMobileIndicator2Ref.current.classList.remove("active");
    }
  }

  function handleSliderChange(swiper: SwiperClass) {
    changeSliderIndicatorActive(swiper.realIndex);
  }

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden component-2-section-bg-gradient mt-[4rem]"
      id="component-2"
    >
      <div className="container-lp mx-auto relative">
        {/* Backgrounds */}
        <div
          ref={content1Ref}
          className="relative w-full mb-[-16rem] md:mb-[-20rem] lg:mb-[-20rem] xl:mb-[-28rem] 2xl:mb-[-20rem]"
        >
          <HLSVideoPlayer
            controls={false}
            playsInline={true}
            autoPlay={true}
            muted={true}
            loop={true}
            className="w-full h-[70vh] lg:h-auto !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
            source={{
              embed: false,
              muxVideo: {
                assetId: "",
                filename: "video-bg",
                status: "ready",
                playbackId: "1OfCrWJKYvirSBMsKn3UsIbmuEBPMoLsquPhlMcqOEs",
              },
              embedUrl: "",
              thumbnail: {
                src: "",
                width: 0,
                height: 0,
                lqip: "",
                name: "",
              },
            }}
          />

          <Image
            src={griBGimg}
            alt="Grid background"
            className="absolute z-[-3] left-0 right-0 bottom-0 w-full h-[50vh] lg:h-auto object-cover pointer-events-none"
          />

          <Image
            src={blurBGimg}
            alt="Blur background"
            className="absolute z-[-2] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none w-max max-w-max lg:w-full lg:max-w-full"
          />
        </div>
        {/* End Backgrounds */}

        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[70%] flex flex-col items-center justify-center mx-auto mb-[4rem]"
          >
            {data?.component2Texts && (
              <PortableText
                value={data?.component2Texts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill text={children as string} />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] lg:leading-[8.875rem] leading-[110%] text-center mt-[4rem] mb-[3rem] w-[65%] lg:w-[75%]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[90%] mx-auto">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span className="purple-shadow">{children}</span>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div ref={content3Ref} className="relative">
            <div className="hidden lg:flex justify-center content-center gap-[1rem] mb-[6rem] z-[2]">
              {data?.component2Items?.map((card, index) => (
                <button
                  key={index + 1}
                  ref={
                    index === 0
                      ? btnIndicator1Ref
                      : index === 1
                        ? btnIndicator2Ref
                        : btnIndicator3Ref
                  }
                  onClick={() => goToSlide(index)}
                  className="custom-tab-indicator swiper-pagination-component-2-indicator relative py-[1.8rem] px-[3.2rem] rounded-[1.5rem] text-[#fff] opacity-90 text-[2rem] font-[600] leading-[120%] uppercase transition duration-300 ease-in-out"
                >
                  {card?.title}
                  <span className="progress-bar"></span>
                  <span className="progress-bg"></span>
                </button>
              ))}
            </div>

            <Swiper
              ref={swiperRef}
              centeredSlides={true}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              onRealIndexChange={handleSliderChange}
              navigation={true}
              modules={[Autoplay, Navigation]}
              onAutoplayTimeLeft={onAutoplayTimeLeft}
              slidesPerView={"auto"}
              className="component-2-swipper w-full max-w-[95vw] overflow-hidden relative"
            >
              {data?.component2Items?.map((card, index) => (
                <SwiperSlide
                  key={index + 1}
                  className="w-full shrink-0 relative"
                >
                  <Card
                    key={index}
                    assetImg={card?.image}
                    imageOrder={2}
                    titleClasses="text-[4.5rem] font-[600] leading-[120%] tracking-[-0.2rem]"
                    hideCTAonMobile={true}
                    ctaData={card?.CTA}
                    texts={card?.texts}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="block lg:hidden">
            <div className="flex justify-center items-center gap-[1rem] mt-[4rem] mb-[5rem] relative z-[2]">
              <button
                onClick={() => goToPreviousSlide()}
                className="w-[6rem] h-[6rem] bg-[#ffffff14] rounded-full flex justify-center items-center mr-[2rem]"
              >
                <Image src={leftArrowImg} alt="Previous" />
              </button>
              {data?.component2Items?.map((_, index) => (
                <button
                  key={index + 1}
                  ref={
                    index === 0
                      ? btnMobileIndicator1Ref
                      : index === 1
                        ? btnMobileIndicator2Ref
                        : btnMobileIndicator3Ref
                  }
                  onClick={() => goToSlide(index)}
                  className="custom-tab-indicator-mobile rounded-[1.5rem]"
                ></button>
              ))}
              <button
                onClick={() => goToNextSlide()}
                className="w-[6rem] h-[6rem] bg-[#ffffff14] rounded-full flex justify-center items-center ml-[2rem]"
              >
                <Image src={rightArrowImg} alt="Next" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

  );
}
